import React, { useEffect, useReducer } from 'react';
import { ENDPOINT_ME, AUTH_URL } from '../../libs/constantes';

const AuthStateContext = React.createContext();
const AuthDispatchContext = React.createContext();

const PREFS_KEY = 'comics_prefs';

const loadPrefs = () => {
    try {
        return JSON.parse(localStorage.getItem(PREFS_KEY)) || { autoSearch: false, editionMode: false };
    } catch {
        return { autoSearch: false, editionMode: false };
    }
};

const initialState = {
    status: 'idle',
    user: null,
    error: null,
    justSignedIn: false,
    userPrefs: loadPrefs(),
};

function reducer(state, action) {
    switch (action.type) {
        case 'AUTH_PENDING':
            return { ...state, status: 'pending' };
        case 'AUTH_RESOLVED':
            return { ...state, status: 'resolved', user: action.payload, justSignedIn: !!action.justSignedIn, error: null };
        case 'AUTH_REJECTED':
            return { ...state, status: 'idle', user: null, justSignedIn: false, error: action.payload || null };
        case 'UPDATE_PREFS': {
            const userPrefs = { ...state.userPrefs, ...action.payload };
            localStorage.setItem(PREFS_KEY, JSON.stringify(userPrefs));
            return { ...state, userPrefs };
        }
        default:
            return state;
    }
}

function useAuthState() {
    const context = React.useContext(AuthStateContext);
    if (!context) throw new Error("useAuthState must be used in AuthProvider");
    return context;
}

function useAuthDispatch() {
    const context = React.useContext(AuthDispatchContext);
    if (!context) throw new Error("useAuthDispatch must be used in AuthProvider");
    return context;
}

// Dernier utilisateur authentifié connu, persisté dans localStorage (partagé
// entre onglets). Sert à détecter une vraie connexion (transition « pas d'user
// connu → user résolu ») quel que soit le chemin emprunté : clic "Sign in",
// redirection auto sur 401, ou simple ouverture d'un nouvel onglet déjà loggé.
const LAST_AUTH_KEY = 'comics_last_auth';

function AuthProvider(props) {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        let cancelled = false;
        dispatch({ type: 'AUTH_PENDING' });
        fetch(ENDPOINT_ME, { credentials: 'include' })
            .then(res => (res.ok ? res.json() : null))
            .then(user => {
                if (cancelled) return;
                if (user) {
                    const previous = localStorage.getItem(LAST_AUTH_KEY);
                    const justSignedIn = previous !== String(user.id);
                    localStorage.setItem(LAST_AUTH_KEY, String(user.id));
                    dispatch({ type: 'AUTH_RESOLVED', payload: user, justSignedIn });
                } else {
                    localStorage.removeItem(LAST_AUTH_KEY);
                    dispatch({ type: 'AUTH_REJECTED' });
                }
            })
            .catch(err => !cancelled && dispatch({ type: 'AUTH_REJECTED', payload: err }));
        return () => { cancelled = true; };
    }, []);

    return (
        <AuthStateContext.Provider value={state}>
            <AuthDispatchContext.Provider value={dispatch}>
                {props.children}
            </AuthDispatchContext.Provider>
        </AuthStateContext.Provider>
    );
}

const currentUrlEncoded = () => encodeURIComponent(window.location.href);

function signIn() {
    window.location.href = `${AUTH_URL}/login?redirect=${currentUrlEncoded()}`;
}

function signOut() {
    window.location.href = `${AUTH_URL}/logout?redirect=${currentUrlEncoded()}`;
}

function setUserPrefs(dispatch, userPrefs) {
    dispatch({ type: 'UPDATE_PREFS', payload: userPrefs });
}

export { AuthProvider, useAuthState, useAuthDispatch, signIn, signOut, setUserPrefs };
